// A customer's own CRM, which VERA builds and runs: Marine (Clients, Boats, Jobs, Materials, Orders) and every client
// pack (DAMA Security: Clients, Sites, Team, Shifts, Patrols, Incidents, Devices, Inventory, Invoices, Leave), plus
// whatever VERA adds. Calm on purpose: tables, records and a log of what changed. No code anywhere: people click, or
// tell VERA. The server decides what each person sees and changes; these screens show it and ask. Words go through
// T() (German from lib/i18n.de.ts); list and field names come from the company's own CRM, in its own language.

import { OwnerPortalCard } from "../components/OwnerPortalCard";
import { BrainCard } from "../components/BrainCard";
import { TeamOnlineCard } from "../components/TeamOnlineCard";
import { seesPresence } from "../lib/presence";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Head, Icon, useStudio } from "../components/ui";
import { Home } from "./Home";
import { vera } from "../api/vera";
import { currentLang, dateLocale, T, Tf } from "../lib/i18n";
import { CLIENT_PACKS } from "../config/clientPacks";
import { CheckpointQrCard, StaffAccessCard, StaffMailCard } from "../components/StaffAccess";
import { Notifications } from "../components/Notifications";
import { VeraField } from "./VeraField";
import { ActivityLog } from "./ActivityLog";
import { RecordsVera } from "../components/RecordsVera";
import { VoiceMode } from "../components/VoiceMode";
import { completeness, missing, usedValues } from "../lib/completeness";
import type { ScopeId } from "../config/model";
import { useHomePrefs } from "../lib/homePrefs";
import { applyView, filterable, filterValues, readView, saveView, UPDATED, type ListView } from "../lib/listView";
import { CLOSED, CRM_CHANGED, countsText, crm, photoUrl, show, titleOf, VIA, type CrmChange, type CrmField, type CrmHome, type CrmPerson, type CrmRecord, type CrmType, type ImportPreview, type Value } from "../lib/crm";

const errorText = (e: unknown) => (e instanceof Error ? e.message : T("VERA's server didn't answer. Please try again."));
const de = () => currentLang() === "de";
const sq = () => currentLang() === "sq";
/** "New shift" / "Neu: Schicht" / "Shto: Klient" (German and Albanian nouns keep their capital). */
const newOf = (name: string) => (de() ? `Neu: ${name}` : sq() ? `Shto: ${name}` : `New ${name.toLowerCase()}`);

/** The CRM's types and the person's role, loaded once per screen. */
function useCrm() {
  const { getToken } = useStudio();
  const [home, setHome] = useState<CrmHome | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setHome(await crm.home(getToken)); setError(""); } catch (e) { setError(errorText(e)); }
  }, [getToken]);
  useEffect(() => { if (vera.mode === "live") void load(); }, [load]);
  // Build mode changed how the CRM is set up: show the new types and fields at once.
  useEffect(() => { const on = () => void load(); window.addEventListener(CRM_CHANGED, on); return () => window.removeEventListener(CRM_CHANGED, on); }, [load]);
  return { home, error, reload: load };
}

/** Every record of the given types, for naming links and choosing them. */
function useLists(types: string[]) {
  const { getToken } = useStudio();
  const [lists, setLists] = useState<Record<string, CrmRecord[]>>({});
  const key = types.join(",");
  const load = useCallback(async () => {
    const next: Record<string, CrmRecord[]> = {};
    await Promise.all(key.split(",").filter(Boolean).map(async (t) => { try { next[t] = await crm.list(getToken, t); } catch { next[t] = []; } }));
    setLists(next);
  }, [getToken, key]);
  useEffect(() => { void load(); }, [load]);
  return { lists, reload: load };
}

function titlesFrom(home: CrmHome | null, lists: Record<string, CrmRecord[]>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const t of home?.schema.types ?? []) for (const r of lists[t.id] ?? []) out[r.id] = titleOf(t, r);
  return out;
}

function NotLive() {
  return <p className="mute">{T("Your records appear here with live VERA.")}</p>;
}

function AskBar({ placeholder }: { placeholder: string }) {
  const { go } = useStudio();
  const [text, setText] = useState("");
  const box = useRef<HTMLInputElement>(null);
  // Never a dead grey button: with nothing typed yet, Ask VERA puts the cursor in the box (people read grey as locked).
  const ask = () => { const t = text.trim(); if (t) go("workspace", { ask: t, fresh: true, title: t.slice(0, 60) }); else box.current?.focus(); };
  return (
    <div className="askbar">
      <Icon name="vera" size={17} />
      <input ref={box} value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }} aria-label={T("Tell VERA")} />
      <button className="btn primary sm" onClick={ask}>{T("Ask VERA")}</button>
    </div>
  );
}

// ---------- Home ----------

/**
 * The list Home shows as open work: Marine's jobs, DAMA's shifts, or the first list with a status and a date.
 * With its date field (sorted by it) and the first link (the boat, the site) under each title.
 */
function workList(types: CrmType[]) {
  const t = types.find((x) => x.id === "jobs") ?? types.find((x) => x.id === "shifts") ?? types.find((x) => x.id === "applications") ?? types.find((x) => x.statusField && x.fields.some((f) => f.kind === "date"));
  return t ? { type: t, date: t.fields.find((f) => f.kind === "date"), link: t.fields.find((f) => f.kind === "link") } : null;
}

export function MarineHome() {
  const { go, getToken, profile, user } = useStudio();
  const { home, error } = useCrm();
  const work = home ? workList(home.schema.types) : null;
  const jobsType = work?.type;
  const pack = CLIENT_PACKS.find((c) => c.key === profile.pack);
  const { lists } = useLists(useMemo(() => (home ? home.schema.types.map((t) => t.id) : []), [home]));
  const [changes, setChanges] = useState<CrmChange[] | null>(null);
  const loadChanges = useCallback(() => { crm.changes(getToken).then(setChanges).catch(() => setChanges([])); }, [getToken]);
  useEffect(() => { if (vera.mode === "live") loadChanges(); }, [loadChanges]);
  const { shows } = useHomePrefs();
  if (vera.mode !== "live") return <NotLive />;
  const titles = titlesFrom(home, lists);
  const status = jobsType?.statusField;
  const dateId = work?.date?.id ?? "scheduled";
  const open = (jobsType ? lists[jobsType.id] ?? [] : []).filter((j) => !status || !CLOSED.test(String(j.values[status] ?? ""))).sort((a, b) => String(a.values[dateId] ?? "9").localeCompare(String(b.values[dateId] ?? "9")));
  // Home is quick catches only: the greeting with time, weather and compass; notifications; her day; a brief report.
  // Setting things up lives in Settings (demo company, what VERA remembers, company safety, the ServiceFusion import),
  // dropped documents in Files, and the framework in Files → Knowledge base. Every card can be hidden in Settings → Home.
  return (
    <>
      {/* Her day first, exactly as on VERA Studio's Home: greeting, three things, what VERA noticed, calendar, inbox, Teams. */}
      <Home afterGreeting={shows("notifications") && home ? <Notifications jobsType={jobsType} dateField={dateId} lists={lists} types={home.schema.types} changes={changes} /> : null} />
      {pack && home?.me.role === "owner" && home.schema.types.some((t) => t.id === "guards") && <StaffMailCard />}
      {error && <p className="err">{error}</p>}
      {/* HQ, for the founder and executives: who on the team has Studio open now, and when the others were last seen. */}
      {shows("online") && seesPresence(user.roles) && <div data-card="online"><TeamOnlineCard /></div>}
      {shows("operations") && home && <div data-card="operations"><BrainCard stamp={changes?.[0]?.id} /></div>}
      {(shows("open") || shows("changes")) && <div className={shows("open") && shows("changes") ? "split" : "stack"}>
        {shows("open") && <section className="card" data-card="open">
          <div className="row between"><h2>{jobsType ? (de() ? `Offen: ${jobsType.plural}` : sq() ? `Të hapura: ${jobsType.plural}` : `Open ${jobsType.plural.toLowerCase()}`) : T("Open work")}</h2>{jobsType && <button className="btn sm" onClick={() => go("records", { crmType: jobsType.id })}>{de() ? `Alle ${jobsType.plural}` : sq() ? `Të gjitha: ${jobsType.plural}` : `All ${jobsType.plural.toLowerCase()}`}</button>}</div>
          {!home ? <p className="faint">{T("Loading…")}</p> : !open.length ? <p className="faint">{T("Nothing open. Tell VERA about the next one.")}</p> : (
            <div className="list">{open.slice(0, 8).map((j) => (
              <button className="li rowbtn" key={j.id} onClick={() => go("records", { crmType: jobsType!.id, crmRecord: j.id })}>
                <div className="grow"><b>{jobsType ? titleOf(jobsType, j) : j.id}</b>
                  <div className="faint small">{[work?.link && j.values[work.link.id] ? titles[String(j.values[work.link.id])] : "", j.values[dateId] ? show({ id: dateId, name: "", kind: "date" }, j.values[dateId], titles, home.people) : ""].filter(Boolean).join(" · ")}</div></div>
                {status && j.values[status] && <span className="tag">{String(j.values[status])}</span>}
              </button>))}
            </div>)}
        </section>}
        {shows("changes") && <section className="card" data-card="changes">
          <div className="row between"><h2>{T("Latest changes")}</h2><button className="btn sm" onClick={() => go("activity")}>{T("See all")}</button></div>
          {changes === null ? <p className="faint">{T("Loading…")}</p> : !changes.length ? <p className="faint">{T("Nothing yet. Every change VERA or the team makes shows here.")}</p> : (
            <div className="list">{changes.filter((c) => c.action !== "undo").slice(0, 3).map((c) => (
              <div className="li" key={c.id}><div className="grow"><span>{c.summary}</span><div className="faint small">{c.who} {T(VIA[c.via] ?? "")} · {ago(c.at)}</div></div></div>))}
            </div>)}
        </section>}
      </div>}
      {shows("records") && home && (
        <div className="typegrid sea" data-card="records">{home.schema.types.map((t, i) => (
          <button key={t.id} className={`card typecard sea-${i % 6}`} onClick={() => go("records", { crmType: t.id })}>
            <span className="small">{t.plural}</span><b className="num">{lists[t.id]?.length ?? "…"}</b>
          </button>))}
        </div>)}
    </>
  );
}

function ago(ms: number) {
  const m = Math.round((Date.now() - ms) / 60000);
  if (m < 1) return de() ? "gerade eben" : sq() ? "tani" : "just now";
  if (m < 60) return de() ? `vor ${m} Min.` : sq() ? `para ${m} min` : `${m} min ago`;
  if (m < 60 * 24) return de() ? `vor ${Math.round(m / 60)} Std.` : sq() ? `para ${Math.round(m / 60)} orësh` : `${Math.round(m / 60)} h ago`;
  return new Date(ms).toLocaleDateString(dateLocale(currentLang()), { month: "short", day: "numeric" });
}

// ---------- Records ----------

export function Records() {
  const { getToken, pending, clearPending, profile, consent, openConsent, toast, user } = useStudio();
  const { home, error, reload: reloadHome } = useCrm();
  const types = home?.schema.types ?? [];
  const [typeId, setTypeId] = useState<string>(pending?.crmType ?? "");
  const type = types.find((t) => t.id === typeId) ?? types[0];
  const linked = useMemo(() => (type ? [...new Set([type.id, ...type.fields.filter((f) => f.linkTo).map((f) => f.linkTo!)])] : []), [type]);
  const { lists, reload } = useLists(linked);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<CrmRecord | "new" | null>(null);
  // VERA right here: typing (the panel beside the list) or talking (voice), about what's on screen.
  const [veraOpen, setVeraOpen] = useState<{ start?: string } | null>(null);
  const [voice, setVoice] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);
  // Sort and Filter: per list and person, staying as they were left.
  const [view, setViewState] = useState<ListView>({});
  useEffect(() => { if (typeId) setViewState(readView(user.id, typeId)); }, [user.id, typeId]);
  const setView = (v: ListView) => { setViewState(v); if (typeId) saveView(user.id, typeId, v); };

  useEffect(() => { if (type && type.id !== typeId) setTypeId(type.id); }, [type, typeId]);
  // Sent to a list while already here (Build mode's "Open Inspections", Home's cards).
  useEffect(() => {
    if (pending?.crmType && !pending.crmRecord && types.some((t) => t.id === pending.crmType)) { setTypeId(pending.crmType); setQ(""); setOpen(null); clearPending(); }
  }, [pending, types, clearPending]);
  // Opened from Home on a record.
  useEffect(() => {
    if (pending?.crmRecord && lists[pending.crmType ?? ""]) {
      const r = lists[pending.crmType ?? ""].find((x) => x.id === pending.crmRecord);
      if (r) setOpen(r);
      clearPending();
    }
  }, [pending, lists, clearPending]);

  if (vera.mode !== "live") return <NotLive />;
  if (error) return <><Head title={T("Records")} /><p className="err">{error}</p></>;
  if (!home || !type) return <><Head title={T("Records")} /><p className="faint">{home ? T("No record types yet. Tell VERA how you work and she'll set them up.") : T("Loading…")}</p>{home && <AskBar placeholder={T("Tell VERA how your work is organized")} />}</>;

  const titles = titlesFrom(home, lists);
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const all = lists[type.id] ?? [];
  const incomplete = all.filter((r) => missing(type, r).length > 0);
  // The fields most often left empty in this list, for the reminder ("most often: date, boat").
  const gaps = Object.entries(incomplete.flatMap((r) => missing(type, r).map((f) => f.name)).reduce<Record<string, number>>((n, f) => ({ ...n, [f]: (n[f] ?? 0) + 1 }), {})).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([f]) => f.toLowerCase());
  const rows = applyView(type, all.filter((r) => (!onlyMissing || missing(type, r).length > 0) && (!words.length || words.every((w) => Object.values(r.values).some((v) => String(v ?? "").toLowerCase().includes(w) || titles[String(v)]?.toLowerCase().includes(w))))), view, titles, home.people);
  const sortable = type.fields.filter((f) => !f.hidden && !["longtext", "photos", "links"].includes(f.kind));
  const filterField = view.filterField ? type.fields.find((f) => f.id === view.filterField) : undefined;
  const sortBy = (id: string) => setView({ ...view, sort: id, desc: view.sort === id ? !view.desc : false });
  const scope = (profile.scopes[0] ?? "all") as ScopeId;
  const openRecord = open && open !== "new" ? open : null;
  const talk = () => { if (consent.mic) setVoice(true); else { toast(T("Turn on the microphone for voice in Privacy & consent first.")); openConsent(); } };
  const columns = [type.fields.find((f) => f.id === type.titleField)!, ...type.fields.filter((f) => !f.hidden && f.id !== type.titleField && !["longtext", "photos", "links"].includes(f.kind)).slice(0, 5)];
  const canAdd = home.me.role === "owner" || type.techsCreate;

  return (
    <>
      <Head title={T("Records")} lead={T("Your CRM, shaped by VERA. Click a row to see or change it, or tell VERA: she puts every detail in the right place.")}>
        <button className="btn rec-talk" onClick={talk} title={T("Talk to VERA about your records")}><Icon name="mic" size={15} /> {T("Talk to VERA")}</button>
        <button className={`btn sm${veraOpen ? " on" : ""}`} onClick={() => setVeraOpen(veraOpen ? null : {})} aria-pressed={Boolean(veraOpen)} title={T("Type to VERA")}><Icon name="chat" size={14} /> {T("Type")}</button>
        {canAdd && <button className="btn primary" onClick={() => { setVeraOpen(null); setOpen("new"); }}><Icon name="plus" size={15} /> {newOf(type.name)}</button>}
      </Head>
      {voice && <VoiceMode scope={scope} getToken={getToken} onClose={() => { setVoice(false); void reload(); void reloadHome(); }} />}
      <div className="seg rec-tabs" role="tablist" aria-label={T("Record types")}>
        {types.map((t, i) => {
          const list = lists[t.id];
          const pct = list ? completeness(t, list) : null;
          return <button key={t.id} role="tab" aria-selected={t.id === type.id} className={`${t.id === type.id ? "on" : ""} sea-${i % 6}`} onClick={() => { setTypeId(t.id); setQ(""); setOpen(null); setOnlyMissing(false); }}>
            {t.plural}{list && <span className="rec-count">{list.length}</span>}{pct !== null && list?.length ? <span className={`rec-pct${pct < 70 ? " low" : ""}`} title={T("How complete the details are")}>{pct}%</span> : null}
          </button>;
        })}
      </div>
      {incomplete.length > 0 && (
        <div className="notice calm rec-missing row between">
          <span><Icon name="sparkle" size={14} /> {incomplete.length} {incomplete.length === 1 ? type.name.toLowerCase() : type.plural.toLowerCase()} {T("still missing details")}{gaps.length ? ` (${T("most often")}: ${gaps.join(", ")})` : ""}.</span>
          <span className="row" style={{ gap: 6 }}>
            <button className={`btn sm${onlyMissing ? " on" : ""}`} aria-pressed={onlyMissing} onClick={() => setOnlyMissing((v) => !v)}>{onlyMissing ? T("Show all") : T("Show only these")}</button>
            <button className="btn sm primary" onClick={() => { setOpen(null); setVeraOpen({ start: Tf("Help me fill in the missing details on my {list}, one at a time.", { list: type.plural.toLowerCase() }) }); }}>{T("Fill them in with VERA")}</button>
          </span>
        </div>)}
      <section className="card flat">
        <div className="row between">
          <label className="filter"><Icon name="search" size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={de() ? `${type.plural} durchsuchen` : sq() ? `Kërko te ${type.plural}` : `Search ${type.plural.toLowerCase()}`} /></label>
          <div className="row rec-view" style={{ gap: 6, flexWrap: "wrap" }}>
            <label className="row small" style={{ gap: 4 }}><span className="faint">{T("Sort")}</span>
              <select value={view.sort ?? ""} onChange={(e) => setView({ ...view, sort: e.target.value || undefined, desc: false })} aria-label={T("Sort by")}>
                <option value="">{T("As added")}</option>
                {sortable.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                <option value={UPDATED}>{T("Last changed")}</option>
              </select></label>
            {view.sort && <button className="btn sm" onClick={() => setView({ ...view, desc: !view.desc })} title={T("Change the order")}>{view.desc ? T("Z to A") : T("A to Z")}</button>}
            <label className="row small" style={{ gap: 4 }}><span className="faint">{T("Filter")}</span>
              <select value={view.filterField ?? ""} onChange={(e) => setView({ ...view, filterField: e.target.value || undefined, filterValue: undefined })} aria-label={T("Filter by")}>
                <option value="">{T("Everything")}</option>
                {filterable(type).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select></label>
            {filterField && <select value={view.filterValue ?? ""} onChange={(e) => setView({ ...view, filterValue: e.target.value || undefined })} aria-label={filterField.name}>
              <option value="">{T("Any")}</option>
              {filterValues(filterField, all, titles, home.people).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>}
            {(view.sort || view.filterField) && <button className="iconbtn ghost" onClick={() => setView({})} title={T("Back to as added, everything")} aria-label={T("Back to as added, everything")}><Icon name="x" size={13} /></button>}
          </div>
          <span className="faint small num">{rows.length} {de() ? "von" : sq() ? "nga" : "of"} {lists[type.id]?.length ?? 0}</span>
        </div>
        {!lists[type.id] ? <p className="faint">{T("Loading…")}</p> : !rows.length ? <p className="faint" style={{ marginTop: 12 }}>{q ? T("Nothing matches.") : de() ? `Noch keine Einträge in ${type.plural}.` : sq() ? `Ende asgjë te ${type.plural}.` : `No ${type.plural.toLowerCase()} yet.`}</p> : (
          <div className="tbl-wrap"><table className="records">
            <thead><tr>{columns.map((c) => <th key={c.id} className="sortable" onClick={() => sortBy(c.id)} aria-sort={view.sort === c.id ? (view.desc ? "descending" : "ascending") : undefined} title={T("Sort by this")}>{c.name}{view.sort === c.id ? (view.desc ? " ↓" : " ↑") : ""}</th>)}{rows.some((r) => r.assignedTo.length) && <th>{T("Given to")}</th>}<th className="right sortable" onClick={() => sortBy(UPDATED)} title={T("Sort by this")}>{T("Changed")}{view.sort === UPDATED ? (view.desc ? " ↓" : " ↑") : ""}</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} tabIndex={0} onClick={() => setOpen(r)} onKeyDown={(e) => { if (e.key === "Enter") setOpen(r); }} className={open !== "new" && open?.id === r.id ? "sel" : ""}>
                {columns.map((c, i) => <td key={c.id} className={i === 0 ? "strong" : c.kind === "money" || c.kind === "number" ? "num" : ""}>{i === 0 && missing(type, r).length > 0 && <span className="rec-miss" title={`${T("Missing")}: ${missing(type, r).map((f) => f.name).join(", ")}`}>{missing(type, r).length}</span>}{c.id === type.statusField && r.values[c.id] ? <span className="tag">{String(r.values[c.id])}</span> : show(c, r.values[c.id], titles, home.people) || (i > 0 && c.kind !== "yesno" ? <span className="rec-empty">—</span> : null)}</td>)}
                {rows.some((x) => x.assignedTo.length) && <td>{r.assignedTo.map((u) => home.people.find((p) => p.userId === u)?.name ?? T("Someone")).join(", ")}</td>}
                <td className="right faint small">{ago(r.updated)}</td>
              </tr>))}
            </tbody>
          </table></div>)}
      </section>
      {veraOpen ? (
        <RecordsVera type={type} record={openRecord} title={openRecord ? titleOf(type, openRecord) : undefined} owner={home.me.role === "owner"} start={veraOpen.start ?? null}
          onChanged={() => { void reload(); void reloadHome(); }} onClose={() => setVeraOpen(null)} />
      ) : open && (
        <RecordPanel key={open === "new" ? "new" : `${open.id}-${open.version}`} type={type} types={types} record={open === "new" ? null : open} home={home} lists={lists} titles={titles}
          onClose={() => setOpen(null)}
          onSaved={(r) => { void reload(); void reloadHome(); setOpen(r); }}
          onRemoved={() => { void reload(); setOpen(null); }} getToken={getToken}
          onAskVera={(start) => setVeraOpen({ start })}
          onAddChoice={async (f, choice) => {
            try { await crm.reshape(getToken, [{ op: "set_options", type: type.id, field: f.id, options: [...(f.options ?? []), choice] }], home.schema.version); await reloadHome(); return true; }
            catch (e) { toast(errorText(e)); return false; }
          }} />
      )}
    </>
  );
}

function RecordPanel({ type, types, record, home, lists, titles, onClose, onSaved, onRemoved, getToken, onAskVera, onAddChoice }: {
  type: CrmType; types: CrmType[]; record: CrmRecord | null; home: CrmHome; lists: Record<string, CrmRecord[]>; titles: Record<string, string>;
  onClose: () => void; onSaved: (r: CrmRecord) => void; onRemoved: () => void; getToken: () => Promise<string | null>;
  onAskVera: (start: string) => void; onAddChoice: (f: CrmField, choice: string) => Promise<boolean>;
}) {
  const { toast, profile } = useStudio();
  const owner = home.me.role === "owner";
  const [editing, setEditing] = useState(!record);
  const [draft, setDraft] = useState<Record<string, Value>>(record?.values ?? {});
  const [given, setGiven] = useState<string[]>(record?.assignedTo ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mine = !record || owner || record.assignedTo.includes(home.me.userId);
  const fields = type.fields.filter((f) => !f.hidden);
  const gaps = record ? missing(type, record) : [];

  async function save() {
    setBusy(true); setError("");
    try {
      const changed: Record<string, Value> = {};
      for (const f of fields) {
        const before = record?.values[f.id] ?? null, after = draft[f.id] ?? null;
        if (JSON.stringify(before) !== JSON.stringify(after)) changed[f.id] = after === "" ? null : after;
      }
      const assign = owner && JSON.stringify(given) !== JSON.stringify(record?.assignedTo ?? []) ? given : undefined;
      const saved = record ? await crm.update(getToken, type.id, record.id, record.version, changed, assign) : await crm.create(getToken, type.id, changed, assign);
      toast(record ? T("Saved.") : de() ? `${type.name} hinzugefügt.` : sq() ? `${type.name}: u shtua.` : `${type.name} added.`);
      setEditing(false);
      onSaved(saved);
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }

  async function remove() {
    if (!record || !confirm(de() ? `Diesen Eintrag (${type.name}) entfernen? Sie können es unter Aktivität rückgängig machen.` : sq() ? `Ta heq këtë (${type.name})? Mund ta zhbësh te Aktiviteti.` : `Remove this ${type.name.toLowerCase()}? It can be undone from Activity.`)) return;
    setBusy(true);
    try { await crm.remove(getToken, type.id, record.id); toast(T("Removed. Undo it from Activity if needed.")); onRemoved(); } catch (e) { setError(errorText(e)); setBusy(false); }
  }

  return (
    <aside className="drawer" aria-label={record ? titleOf(type, record) : newOf(type.name)}>
      <div className="row between">
        <div><span className="faint small">{type.name}</span><h2>{record ? titleOf(type, record) : newOf(type.name)}</h2></div>
        <button className="iconbtn ghost" onClick={onClose} aria-label={T("Close")}><Icon name="x" /></button>
      </div>
      {error && <p className="err">{error}</p>}
      {record && !editing && gaps.length > 0 && (
        <div className="notice calm rec-missing" style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
          <span><b>{gaps.length === 1 ? T("1 detail missing") : Tf("{n} details missing", { n: String(gaps.length) })}</b>: {gaps.map((f) => f.name).join(", ")}</span>
          <span className="row" style={{ gap: 6 }}>
            {mine && <button className="btn sm" onClick={() => setEditing(true)}>{T("Fill them in")}</button>}
            <button className="btn sm primary" onClick={() => onAskVera(Tf("What's still missing on this {kind}? Ask me for it one by one.", { kind: type.name.toLowerCase() }))}><Icon name="vera" size={13} /> {T("Ask VERA")}</button>
          </span>
        </div>)}
      <dl className="fields">
        {fields.map((f) => (
          <div key={f.id} className={f.kind === "longtext" || f.kind === "photos" ? "wide" : ""}>
            <dt>{f.name}{gaps.includes(f) && !editing && <span className="rec-miss-word">{T("missing")}</span>}</dt>
            <dd>{editing && f.kind !== "photos"
              ? <FieldInput field={f} value={draft[f.id] ?? null} onChange={(v) => setDraft((d) => ({ ...d, [f.id]: v }))} options={f.linkTo ? (lists[f.linkTo] ?? []) : []} linkType={types.find((t) => t.id === f.linkTo)} people={home.people}
                  used={f.kind === "text" ? usedValues(lists[type.id] ?? [], f) : []} onAddChoice={owner && f.kind === "choice" ? (c) => onAddChoice(f, c) : undefined} />
              : f.kind === "photos" ? <Photos ids={(record?.values[f.id] as string[] | undefined) ?? []} getToken={getToken} />
              : f.kind === "longtext" ? <p className="pre">{String(record?.values[f.id] ?? "") || <span className="faint">—</span>}</p>
              : show(f, record?.values[f.id], titles, home.people) || <span className="faint">—</span>}</dd>
          </div>))}
        {(owner || (record?.assignedTo.length ?? 0) > 0) && (
          <div className="wide"><dt>{T("Given to")}</dt><dd>{editing && owner
            ? <div className="row wrap">{home.people.map((p) => <label key={p.userId} className="check"><input type="checkbox" checked={given.includes(p.userId)} onChange={(e) => setGiven((g) => e.target.checked ? [...g, p.userId] : g.filter((x) => x !== p.userId))} /> {p.name}</label>)}</div>
            : (record?.assignedTo ?? []).map((u) => home.people.find((p) => p.userId === u)?.name ?? T("Someone")).join(", ") || <span className="faint">{T("Nobody yet")}</span>}</dd></div>)}
        {/* Client packs: staff app access on a Team member, and a checkpoint's QR code (owners). */}
        {record && owner && (profile.pack || profile.key === "client-marine") && type.id === "guards" && <StaffAccessCard guard={record} />}
        {record && owner && profile.pack && type.id === "checkpoints" && <CheckpointQrCard checkpoint={record} siteName={titles[String(record.values.site ?? "")]} />}
        {record && owner && type.id === "clients" && CLIENT_PACKS.find((c) => c.key === profile.pack)?.portal && <OwnerPortalCard client={record} />}
      </dl>
      {record && <p className="faint small">{T("Changed")} {ago(record.updated)} · {T("version")} {record.version}</p>}
      <div className="row end">
        {editing ? <>
          {record && <button className="btn" onClick={() => { setEditing(false); setDraft(record.values); setGiven(record.assignedTo); }}>{T("Cancel")}</button>}
          <button className="btn primary" disabled={busy} onClick={() => void save()}>{busy ? T("Saving…") : record ? T("Save") : T("Add")}</button>
        </> : <>
          {owner && <button className="btn" disabled={busy} onClick={() => void remove()}>{T("Remove")}</button>}
          {mine && <button className="btn primary" onClick={() => setEditing(true)}>{T("Edit")}</button>}
        </>}
      </div>
    </aside>
  );
}

function FieldInput({ field: f, value, onChange, options, linkType, people, used = [], onAddChoice }: {
  field: CrmField; value: Value; onChange: (v: Value) => void; options: CrmRecord[]; linkType?: CrmType; people: CrmPerson[];
  /** Values already used in this text field: offered as a dropdown while typing. */
  used?: string[];
  /** Owners: add a new choice to this dropdown, for everyone (it's how the CRM grows with the work). */
  onAddChoice?: (choice: string) => Promise<boolean>;
}) {
  const s = value === null || value === undefined ? "" : String(value);
  const [adding, setAdding] = useState(false);
  const [choice, setChoice] = useState("");
  if (f.kind === "choice" && adding) return (
    <span className="row" style={{ gap: 6 }}>
      <input autoFocus value={choice} placeholder={T("New choice")} onChange={(e) => setChoice(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setAdding(false); }} />
      <button type="button" className="btn sm primary" disabled={!choice.trim()} onClick={() => void onAddChoice?.(choice.trim()).then((ok) => { if (ok) { onChange(choice.trim()); setAdding(false); setChoice(""); } })}>{T("Add")}</button>
      <button type="button" className="btn sm" onClick={() => setAdding(false)}>{T("Cancel")}</button>
    </span>);
  if (f.kind === "text" && used.length) return (
    <><input value={s} list={`used-${f.id}`} onChange={(e) => onChange(e.target.value)} /><datalist id={`used-${f.id}`}>{used.map((u) => <option key={u} value={u} />)}</datalist></>);
  switch (f.kind) {
    case "longtext": return <textarea value={s} rows={4} onChange={(e) => onChange(e.target.value)} />;
    case "number": case "money": return <input type="number" step={f.kind === "money" ? "0.01" : "any"} value={s} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
    case "date": return <input type="date" value={s.slice(0, 10)} onChange={(e) => onChange(e.target.value || null)} />;
    case "choice": return <select value={s} onChange={(e) => { if (e.target.value === "\u0000new") { setAdding(true); return; } onChange(e.target.value || null); }}><option value="">—</option>{(f.options ?? []).map((o) => <option key={o}>{o}</option>)}{onAddChoice && <option value={"\u0000new"}>{T("+ New choice…")}</option>}</select>;
    case "yesno": return <label className="check"><input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} /> {T("Yes")}</label>;
    case "link": return <select value={s} onChange={(e) => onChange(e.target.value || null)}><option value="">—</option>{options.map((r) => <option key={r.id} value={r.id}>{linkType ? titleOf(linkType, r) : r.id}</option>)}</select>;
    case "links": return <select multiple value={Array.isArray(value) ? value : []} onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}>{options.map((r) => <option key={r.id} value={r.id}>{linkType ? titleOf(linkType, r) : r.id}</option>)}</select>;
    case "person": return <select value={s} onChange={(e) => onChange(e.target.value || null)}><option value="">—</option>{people.map((p) => <option key={p.userId} value={p.userId}>{p.name}</option>)}</select>;
    case "email": return <input type="email" value={s} onChange={(e) => onChange(e.target.value)} />;
    case "url": return <input type="url" value={s} onChange={(e) => onChange(e.target.value)} />;
    case "phone": return <input type="tel" value={s} onChange={(e) => onChange(e.target.value)} />;
    default: return <input value={s} onChange={(e) => onChange(e.target.value)} />;
  }
}

function Photos({ ids, getToken }: { ids: string[]; getToken: () => Promise<string | null> }) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    let live = true;
    void (async () => { for (const id of ids) { const u = await photoUrl(getToken, id); if (live && u) setUrls((m) => ({ ...m, [id]: u })); } })();
    return () => { live = false; };
  }, [ids, getToken]);
  if (!ids.length) return <span className="faint">{T("None yet. VERA files photos from the glasses here.")}</span>;
  return <div className="thumbs">{ids.map((id) => urls[id] ? <a key={id} href={urls[id]} target="_blank" rel="noreferrer"><img src={urls[id]} alt={T("Photo from the field")} /></a> : <span key={id} className="thumb-empty" />)}</div>;
}

// ---------- From the field ----------

/** VERA Field (was "From the field"): see areas/VeraField.tsx. */
export function Field() {
  return <VeraField />;
}

// ---------- Service Fusion, once ----------

/**
 * The one-time move from Service Fusion. Two steps on purpose: VERA reads Service Fusion and shows what would come in
 * (nothing is saved), then the owner says yes to exactly that. Running it again only adds what's new; Activity has
 * one Undo for the whole import.
 */
/** Service Fusion in Connections (Marine owners): the same import, run again any time for what's new. */
export function ServiceFusionConnection() {
  const { home, error, reload } = useCrm();
  if (vera.mode !== "live") return null;
  return (
    <div className="stack"><span className="label">{T("Service Fusion · your jobs and schedule")}</span>
      {error && <p className="err">{error}</p>}
      {!home ? <p className="faint">{T("Loading…")}</p>
        : home.me.role !== "owner" ? <p className="faint">{T("An owner of your workspace brings in Service Fusion.")}</p>
        : <ImportServiceFusion home={home} connection onDone={() => { void reload(); window.dispatchEvent(new Event(CRM_CHANGED)); }} />}
    </div>
  );
}

export function ImportServiceFusion({ home, onDone, connection }: { home: CrmHome; onDone: () => void; connection?: boolean }) {
  const { getToken, toast } = useStudio();
  const [step, setStep] = useState<"idle" | "reading" | "preview" | "importing">("idle");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState("");
  const types = home.schema.types;
  async function look() {
    setStep("reading"); setError("");
    try { setPreview(await crm.previewSf(getToken)); setStep("preview"); } catch (e) { setError(errorText(e)); setStep("idle"); }
  }
  async function bring() {
    if (!preview) return;
    setStep("importing"); setError("");
    try {
      const run = await crm.importSf(getToken, preview.planId);
      toast(run.summary); setPreview(null); setStep("idle"); onDone();
    } catch (e) { setError(errorText(e)); setStep("preview"); }
  }
  const counts = preview ? countsText(preview.adds, types) : "";
  const fields = preview ? Object.entries(preview.customFields) : [];
  return (
    <section className="card stack">
      {connection
        ? <div className="conn-top"><span className="logo" style={{ background: "#1f7a4d" }}>SF</span><div style={{ minWidth: 0 }}><b>Service Fusion</b><div className="faint small">{T("Customers, boats, jobs with materials, notes and checklists, estimates and invoices")}</div></div></div>
        : <h2>{T("Bring in Service Fusion")}</h2>}
      {step === "idle" && <>
        <p className="mute">{connection
          ? T("VERA reads your customers, boats and all your jobs from Service Fusion (with the materials and services used, notes, checklists and job categories), your estimates and your invoices, and shows you what would come in. Run it again any time to bring in what's new; what's already here stays as it is. Nothing is saved until you say so.")
          : T("VERA reads your whole Service Fusion history: customers, boats, every job with the materials used, notes and checklists, your estimates and your invoices (paid and unpaid). She shows you what would come in first; nothing is saved until you say so, and one undo in Activity takes it back.")}</p>
        <button className="btn primary" onClick={() => void look()}>{connection ? T("Bring in from Service Fusion") : T("Look at Service Fusion")}</button>
      </>}
      {step === "reading" && <p className="faint">{T("Reading Service Fusion… this can take a minute.")}</p>}
      {(step === "preview" || step === "importing") && preview && <>
        <p>{counts ? <>{T("VERA would add")}{" "}<b>{counts}</b>.</> : T("Everything in Service Fusion is already here.")}{preview.alreadyHere > 0 && <span className="faint"> {preview.alreadyHere.toLocaleString("en-US")}{" "}{T("already here stay as they are.")}</span>}</p>
        <div className="kv">
          {fields.length > 0 && <><b>{T("Your fields")}</b><span>{fields.map(([f, to]) => `${f} → ${to}`).join(" · ")}</span></>}
          {preview.newStatuses.length > 0 && <><b>{T("New statuses")}</b><span>{preview.newStatuses.join(", ")}{" "}{T("(added to Jobs)")}</span></>}
          {preview.techsMatched.length > 0 && <><b>{T("Work given to")}</b><span>{preview.techsMatched.join(", ")}</span></>}
          {preview.techsNotSignedIn.length > 0 && <><b>{T("Not signed in yet")}</b><span>{preview.techsNotSignedIn.join(", ")}{T(": their names go in the job's notes, so you can give them the work once they sign in.")}</span></>}
        </div>
        {preview.warnings.length > 0 && <details><summary className="faint small">{preview.warnings.length}{" "}{T("things to know")}</summary><ul className="small">{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></details>}
        <div className="row" style={{ gap: 8 }}>
          {counts && <button className="btn primary" disabled={step === "importing"} onClick={() => void bring()}>{step === "importing" ? T("Bringing it in…") : Tf("Bring in {what}", { what: counts })}</button>}
          <button className="btn" disabled={step === "importing"} onClick={() => { setPreview(null); setStep("idle"); }}>{T("Not now")}</button>
        </div>
      </>}
      {error && <p className="err">{error}</p>}
    </section>
  );
}

// ---------- Activity ----------

/** Activity: the day's log and backlog (see areas/ActivityLog.tsx). */
export function Activity() {
  return <ActivityLog />;
}
